/* eslint-disable react/no-unstable-nested-components */
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import {
  ColumnDef,
  ColumnFiltersState,
  ColumnOrderState,
  flexRender,
  functionalUpdate,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  PaginationState,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import { isEmpty, isEqual, isFunction } from 'lodash';
import { ArrowDownWideNarrow, ArrowUpWideNarrow, ChevronsUpDown, HelpCircle } from 'lucide-react';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';

import { ShipmentLabel } from '@/components/modules/shipment/label';
import { ShipmentIcon } from '@/components/modules/shipment/table/icon';
import { ShipmentTableSettingsDrawer } from '@/components/modules/shipment/table/settings-drawer';
import { ShipmentTableTags } from '@/components/modules/shipment/table/tags';
import { Timeline } from '@/components/modules/timeline';
import { Tooltip } from '@/components/tooltip';
import { AccordionContent } from '@/components/ui/accordion';
import { FilterData, FilterToolbar } from '@/components/ui/data-table/filter-toolbar';
import { DataTablePagination } from '@/components/ui/data-table/pagination';
import { HighlightedText } from '@/components/ui/highlighted-text';
import { ImageWithFallback } from '@/components/ui/image-with-fallback';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TruncatedText } from '@/components/ui/truncated-text';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useShipmentTableSettings } from '@/hooks/use-shipment-table-settings';
import { DATA_TABLE_LAYOUTS, SHIPMENT_COLUMNS, SHIPMENT_GROUPING, SHIPMENT_STATUS, SORTING } from '@/lib/constants';
import { FEATURE_FLAGS } from '@/lib/constants/feature-flags';
import { ShipmentGrouping, ShipmentModel } from '@/lib/models/shipment/types';
import { transformShipmentsToTimelineItems } from '@/lib/transformers/timeline-item';
import { TableLayout } from '@/types';
import { cn, tv } from '@/utils/styles';

import { ShipmentArrivingAt } from './arriving-at';
import { ShipmentPortName } from './port-name';
import { ShipmentStatusIndicator } from './status-indicator';

export interface ShipmentsTableProps {
  data: ShipmentModel[];
  onClickDetails?: (value: ShipmentModel) => void;
  onPaginationChange: Dispatch<SetStateAction<PaginationState>>;
  pagination: PaginationState;
  pageCount: number;
  onSortingChange?: (value: SortingState) => void;
  sorting: SortingState;
  filterData?: FilterData;
  onColumnFiltersChange?: (value: ColumnFiltersState) => void;
  columnFilters?: ColumnFiltersState;
  onUpdateShipmentTags?: (args: { labels: string[]; shipmentId: string }) => Promise<void>;
  shouldShowSaveFilters?: boolean;
  onSaveFilters?: () => void;
  saveFiltersLabel?: string;
  shouldShowResetFilters?: boolean;
  onResetFilters?: () => void;
  areFiltersVisible?: boolean;
  layout?: TableLayout;
  onLayoutChange?: (layout: TableLayout) => void;
  grouping?: ShipmentGrouping;
  onGroupingChange?: (grouping: ShipmentGrouping) => void;
  isTableSettingsOpen?: boolean;
  variant?: 'default' | 'shared';
}

const COLUMN_VISIBILITY_DEFAULTS: Record<string, VisibilityState> = {
  [DATA_TABLE_LAYOUTS.TABLE]: {
    id: false,
    createdAt: false,
    skus: false,
    purchaseOrders: false,
  },
  [DATA_TABLE_LAYOUTS.TIMELINE]: {
    id: false,
    status: false,
    carrier: false,
    tags: false,
    createdAt: false,
    skus: false,
    purchaseOrders: false,
  },
} as const;

const ShipmentsTable = ({
  data,
  onClickDetails,
  onPaginationChange,
  pagination,
  pageCount,
  onSortingChange,
  sorting,
  onColumnFiltersChange,
  columnFilters,
  filterData,
  onUpdateShipmentTags,
  shouldShowSaveFilters,
  onSaveFilters,
  saveFiltersLabel,
  shouldShowResetFilters,
  onResetFilters,
  areFiltersVisible,
  layout: tableLayout,
  onLayoutChange,
  grouping,
  onGroupingChange,
  isTableSettingsOpen = false,
  variant = 'default',
}: ShipmentsTableProps) => {
  const layout = tableLayout || 'table';
  const { variant: isShipmentMetaEnabled } = useFeatureFlag(FEATURE_FLAGS.SHIPMENT_META);
  const [isEditColumnsOpen, setIsEditColumnsOpen] = useState(isTableSettingsOpen);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(COLUMN_VISIBILITY_DEFAULTS.table);
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([]);
  const { tableSettings, update } = useShipmentTableSettings();

  const tableColumns: ColumnDef<ShipmentModel>[] = useMemo(() => {
    const isTimeline = layout === DATA_TABLE_LAYOUTS.TIMELINE;
    const textFilter = (
      columnFilters?.find(({ id }) => id === SHIPMENT_COLUMNS.SHIPMENT_ID)?.value as string[] | undefined
    )?.[0];

    const shipmentMetaFields: ColumnDef<ShipmentModel>[] = [
      {
        id: 'purchaseOrders',
        header: 'Purchase orders',
        accessorKey: SHIPMENT_COLUMNS.PURCHASE_ORDERS,
        enableSorting: false,
        enableHiding: true,
        meta: {
          filterPlaceholder: 'Purchase orders',
        },
        cell: ({ row }) => {
          const values = row.original.purchaseOrders || [];

          if (isEmpty(values)) {
            return '-';
          }

          return (
            <div className="flex flex-wrap gap-1">
              {values.map((value, i) => {
                return (
                  <span key={i}>
                    <HighlightedText className="text-sm" text={value} highlight={textFilter} />
                    {i === values.length - 1 ? '' : ','}
                  </span>
                );
              })}
            </div>
          );
        },
      },
      {
        id: 'skus',
        header: 'SKUs',
        accessorKey: SHIPMENT_COLUMNS.SKUS,
        enableSorting: false,
        enableHiding: true,
        meta: {
          filterPlaceholder: 'SKUs',
        },
        cell: ({ row }) => {
          const values = row.original.skus || [];

          if (isEmpty(values)) {
            return '-';
          }

          return (
            <div className="flex flex-wrap gap-1">
              {values.map((value, i) => {
                return (
                  <span key={i}>
                    <HighlightedText className="text-sm" text={value} highlight={textFilter} />
                    {i === values.length - 1 ? '' : ','}
                  </span>
                );
              })}
            </div>
          );
        },
      },
    ];

    const tagsFields: ColumnDef<ShipmentModel>[] = [
      {
        id: 'tags',
        accessorKey: SHIPMENT_COLUMNS.TAGS,
        enableSorting: false,
        header: 'Tags',
        meta: {
          filterPlaceholder: 'Tags',
        },
        cell: ({ cell, row }) => {
          const canExpand = row.getCanExpand();
          if (!canExpand) {
            const shipmentId = row.original.id;
            const selectedTags = (cell.renderValue() || []) as string[];

            const handleUpdateTags = (labels: string[]) => {
              if (shipmentId && isFunction(onUpdateShipmentTags) && !isEqual(labels, selectedTags)) {
                onUpdateShipmentTags({ labels, shipmentId });
              }
            };

            return shipmentId ? <ShipmentTableTags initialTags={selectedTags} onUpdateTags={handleUpdateTags} /> : null;
          }
          return null;
        },
      },
    ];

    const columns: ColumnDef<ShipmentModel>[] = [
      {
        accessorKey: 'id',
        enableColumnFilter: false,
        enableSorting: false,
        enableHiding: false,
        id: 'id',
      },
      {
        id: 'type',
        accessorKey: 'icon',
        enableSorting: false,
        enableHiding: false,
        header: '',
        size: 48,
        cell: ({ row }) => {
          const { type, status, associatedContainers } = row.original as ShipmentModel;
          const childContainerCount = associatedContainers?.length;
          const isExpanded = row.getIsExpanded();
          const canExpand = row.getCanExpand();

          return (
            <ShipmentIcon
              type={type}
              isCompleted={status === SHIPMENT_STATUS.COMPLETED}
              count={childContainerCount}
              canExpand={canExpand}
              isExpanded={isExpanded}
              onToggleExpand={(e) => {
                e.stopPropagation();
                row.toggleExpanded(!isExpanded);
              }}
            />
          );
        },
      },
      {
        id: 'shipmentId',
        accessorKey: SHIPMENT_COLUMNS.SHIPMENT_ID,
        header: 'Shipment Id',
        enableSorting: false,
        enableHiding: false,
        size: 180,
        meta: {
          filterVariant: 'text',
          filterOrder: 1,
          filterPlaceholder: 'ID, PO or SKU...',
        },
        cell: ({ row }) => {
          return <ShipmentLabel shipment={row.original as ShipmentModel} highlightText={textFilter} />;
        },
      },
      {
        id: 'status',
        accessorKey: SHIPMENT_COLUMNS.STATUS,
        header: 'Status',
        meta: {
          filterVariant: 'status',
          filterPlaceholder: 'Status',
        },
        cell: ({ row }) => {
          const { status, subStatus } = row.original as ShipmentModel;
          if (row.depth > 0) {
            return '';
          }
          return <ShipmentStatusIndicator status={status} subStatus={subStatus} compact={isTimeline} />;
        },
      },
      {
        id: 'originPortCode',
        accessorKey: SHIPMENT_COLUMNS.ORIGIN_PORT_CODE,
        header: 'POL',
        size: isTimeline ? 80 : undefined,
        cell: ({ row }) => {
          const { originPortCode, originPortName } = row.original as ShipmentModel;
          if (row.depth > 0) {
            return '';
          }
          return <ShipmentPortName portCode={originPortCode} portName={originPortName} portCodeOnly={isTimeline} />;
        },
      },
      {
        id: 'destinationPortCode',
        accessorKey: SHIPMENT_COLUMNS.DESTINATION_PORT_CODE,
        header: 'POD',
        size: isTimeline ? 80 : undefined,
        cell: ({ row }) => {
          const { destinationPortCode, destinationPortName } = row.original as ShipmentModel;
          if (row.depth > 0) {
            return '';
          }
          return (
            <ShipmentPortName portCode={destinationPortCode} portName={destinationPortName} portCodeOnly={isTimeline} />
          );
        },
      },
      {
        id: 'carrier',
        accessorKey: SHIPMENT_COLUMNS.CARRIER,
        header: 'Carrier',
        cell: ({ row, getValue }) => {
          const { type } = row.original as ShipmentModel;
          const value = getValue() as string;
          if (row.depth > 0) {
            return '';
          }

          if (type === 'parcel') {
            return (
              <div className="flex min-w-0 items-center gap-2">
                <ImageWithFallback
                  fallbackSrc="/logos/mail-carriers/missing.png"
                  src={`/logos/mail-carriers/${value}_logo.png`}
                  alt={value || 'Carrier logo'}
                  width={16}
                  height={16}
                  className="flex-shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <TruncatedText content={value}>
                    <span className="block truncate">{value}</span>
                  </TruncatedText>
                </div>
              </div>
            );
          }
          return value || '-';
        },
      },
      {
        id: 'arriving',
        accessorKey: SHIPMENT_COLUMNS.ARRIVING,
        header: 'Arriving',
        size: 155,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const { eta, delay, ata, originalEta, trackingUpdatedAt, type } = row.original as ShipmentModel;
          if (row.depth > 0) {
            return '';
          }

          return (
            <ShipmentArrivingAt
              eta={eta}
              ata={ata}
              delay={delay}
              modeType={type}
              originalEta={originalEta}
              trackingUpdatedAt={trackingUpdatedAt}
              showUpdatedAt={!isTimeline}
            />
          );
        },
      },
      ...(variant === 'default' ? tagsFields : []),
      ...(isShipmentMetaEnabled ? shipmentMetaFields : []),
    ];
    return columns;
  }, [layout, onUpdateShipmentTags, isShipmentMetaEnabled]);

  const defaultColumnIds = tableColumns.filter((col) => col.enableHiding === false).map((col) => col.id || '');
  const timelineData = useMemo(() => transformShipmentsToTimelineItems(data), [data]);

  const table = useReactTable({
    data,
    columns: tableColumns,
    getSubRows: (row) => row.associatedContainers,
    getRowCanExpand: (row) => {
      return Boolean(row.original.associatedContainers?.length);
    },
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    enableColumnFilters: true,
    onSortingChange: (updaterFunction) => {
      const newValue = functionalUpdate(updaterFunction, sorting);
      if (isFunction(onSortingChange)) {
        onSortingChange(newValue as SortingState);
      }
    },
    onColumnVisibilityChange: setColumnVisibility,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: (updaterFunction) => {
      const newValue = functionalUpdate(updaterFunction, columnFilters || []);
      if (isFunction(onColumnFiltersChange)) {
        onColumnFiltersChange(newValue);
      }
    },
    onColumnOrderChange: (updaterFunction) => {
      const newValue = functionalUpdate(updaterFunction, columnOrder || []);
      setColumnOrder([...defaultColumnIds, ...newValue]);
    },
    state: {
      sorting,
      pagination,
      columnVisibility: {
        ...columnVisibility,
        expand: grouping !== SHIPMENT_GROUPING.NONE,
      },
      columnOrder,
      columnFilters,
    },
    defaultColumn: {
      sortDescFirst: true,
    },
    onPaginationChange,
    manualPagination: true,
    pageCount: pageCount ?? -1,
    manualSorting: true,
    enableRowSelection: false,
    debugTable: true,
  });

  useEffect(() => {
    const settings = tableSettings?.[layout];
    setColumnVisibility(settings?.columnVisibility || COLUMN_VISIBILITY_DEFAULTS[layout]);

    if (settings?.columnOrder.length) {
      setColumnOrder(settings.columnOrder);
    }
  }, [tableSettings, layout]);

  useEffect(() => {
    const settings = tableSettings?.[layout];
    if (!isEqual(settings?.columnOrder, columnOrder) || !isEqual(settings?.columnVisibility, columnVisibility)) {
      update({ ...tableSettings, [layout]: { columnOrder, columnVisibility } });
    }
  }, [columnOrder, columnVisibility, layout]);

  const visibleColumnCount = table.getAllColumns().filter((col) => col.getIsVisible()).length;

  const { tableWrapper } = styles({ layout });

  return (
    <>
      <AccordionPrimitive.Root type="single" value={areFiltersVisible ? 'filters' : undefined}>
        <AccordionPrimitive.AccordionItem value="filters">
          <AccordionContent className="py-0">
            <FilterToolbar
              columns={table.getAllColumns()}
              filterData={filterData || {}}
              onClear={table.resetColumnFilters}
              hasFilters={table.getState().columnFilters?.length !== 0}
              shouldShowSaveFilters={shouldShowSaveFilters}
              onSaveFilters={onSaveFilters}
              saveFiltersLabel={saveFiltersLabel}
              shouldShowResetFilters={shouldShowResetFilters}
              onResetFilters={onResetFilters}
              sorting={sorting}
              onSortingChange={onSortingChange}
              layout={layout}
              onLayoutChange={onLayoutChange}
              grouping={grouping}
              onGroupingChange={onGroupingChange}
              onEditColumns={() => setIsEditColumnsOpen(true)}
            />
          </AccordionContent>
        </AccordionPrimitive.AccordionItem>
      </AccordionPrimitive.Root>
      <div className={tableWrapper()}>
        <Table variant="tracking" className="mb-3">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = isFunction(onSortingChange) && header.column.getCanSort();
                  return (
                    <TableHead
                      key={header.id}
                      style={{ width: `${header.getSize()}px` }}
                      className={cn('relative leading-4', {
                        'cursor-pointer': canSort,
                      })}
                      onClick={header.column.getToggleSortingHandler()}
                      title={(() => {
                        if (!canSort) return undefined;
                        if (header.column.getNextSortingOrder() === SORTING.ASC) return 'Sort ascending';
                        if (header.column.getNextSortingOrder() === SORTING.DESC) return 'Sort descending';
                        return 'Clear sort';
                      })()}
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      {canSort ? (
                        <span className="pl-1">
                          {{
                            asc: <ArrowUpWideNarrow size={16} />,
                            desc: <ArrowDownWideNarrow size={16} />,
                          }[header.column.getIsSorted() as string] ?? <ChevronsUpDown size={16} />}
                        </span>
                      ) : null}
                      {header.column.columnDef.meta?.headerTooltip ? (
                        <Tooltip content={header.column.columnDef.meta?.headerTooltip} maxWidth="280px">
                          <div className="group absolute right-0 top-0 flex size-8 items-center justify-center">
                            <HelpCircle size={16} className="z-4 text-grey-400 group-hover:text-lightBlue-600" />
                          </div>
                        </Tooltip>
                      ) : null}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => {
                return (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    onClick={onClickDetails ? () => onClickDetails(row.original) : undefined}
                    className={cn(
                      variant === 'default' ? 'cursor-pointer hover:!bg-grey-50' : null,
                      table.options?.meta?.getRowClassNames?.(row),
                      {
                        '!bg-lightBlue-200': row.getIsSelected(),
                        '!bg-grey-100': row.depth > 0,
                      },
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} tabIndex={-1}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={visibleColumnCount} className="h-24 text-center">
                  No results
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {layout === DATA_TABLE_LAYOUTS.TIMELINE && (
          <div className="relative">
            <Timeline items={timelineData} scale="week" onRowClick={onClickDetails} />
          </div>
        )}
      </div>
      {pagination && (
        <div
          className={cn('absolute bottom-0 left-0 right-0 z-5', {
            hidden: !table.getRowModel().rows?.length,
          })}
        >
          <DataTablePagination table={table} className={variant === 'shared' ? 'bg-transparent' : ''} />
        </div>
      )}
      <ShipmentTableSettingsDrawer isOpen={isEditColumnsOpen} onOpenChange={setIsEditColumnsOpen} table={table} />
    </>
  );
};

const styles = tv({
  slots: {
    tableWrapper: 'mb-10 overflow-auto',
  },
  variants: {
    layout: {
      table: {
        tableWrapper: '',
      },
      timeline: {
        tableWrapper: 'grid min-w-[900px] grid-cols-[520px_calc(100%-520px)]',
      },
    },
  },
});

export { ShipmentsTable };
